/* eslint-disable complexity */
import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';
import Box from 'carbon-react/lib/components/box';
import { Tab, TabList, TabPanel, Tabs } from 'carbon-react/lib/components/tabs/__next__';

import { Forecast } from '@futrli-sdk/predict';
import { ConsolidatedForecastListItem } from '@futrli-sdk/consolidations';

import * as styles from './forecasting-navigation-tabs.scss';
import { PREDICTIONS_SUB_PAGES_IDS } from './const';
import { PredictionsExplanation } from './prediction-explanation';
import { useForecastingPages } from './use-forecasting-pages';
import { getSelectedTabId } from './helpers';

interface Props {
  topOffset: number;
  isConsolidation?: boolean;
  isBudget: boolean;
  isScenario: boolean;
  isBudgetDateSelect: boolean;
  showInvoices?: boolean;
  handleTabClick: (route: string) => void;
  showPredictionsExplanation?: boolean;
  handlePredictionExplanationsToggle?: () => void;
  forecast?: Forecast | ConsolidatedForecastListItem | null;
  disabled?: boolean;
}

export const NavigationTabs = ({
  topOffset,
  isConsolidation,
  isBudget,
  isScenario,
  isBudgetDateSelect,
  showInvoices,
  handleTabClick,
  showPredictionsExplanation,
  handlePredictionExplanationsToggle,
  forecast,
  disabled
}: Props) => {
  const { pathname } = useLocation();
  const intl = useIntl();

  const forecastingPages = useForecastingPages();

  const handleCarbonTabChange = useCallback(
    (tabId: string) => {
      handleTabClick(tabId);
    },
    [handleTabClick]
  );

  const visiblePages = forecastingPages.filter(page => {
    if (
      page.id === PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_INVOICES &&
      (isConsolidation ||
        isBudget ||
        isScenario ||
        isBudgetDateSelect ||
        (typeof showInvoices === 'boolean' && !showInvoices))
    ) {
      return false;
    }
    if (page.id === PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_ASSETS && (isBudget || isBudgetDateSelect)) {
      return false;
    }
    if (page.id === PREDICTIONS_SUB_PAGES_IDS.PREDICTIONS_LIABILITIES && (isBudget || isBudgetDateSelect)) {
      return false;
    }
    if (page.id === PREDICTIONS_SUB_PAGES_IDS.PREDICTION_BALANCE_SHEET && (isBudget || isBudgetDateSelect)) {
      return false;
    }
    if (page.id === PREDICTIONS_SUB_PAGES_IDS.PREDICTION_CASH_FLOW && (isBudget || isBudgetDateSelect)) {
      return false;
    }
    return true;
  });

  return (
    <Box className={styles.carbonStickyContainer}>
      <Box
        className={classNames(styles.carbonContainer, styles.carbonTabsBar, { [styles.carbonTabsDisabled]: disabled })}
        top={topOffset}
      >
        <Tabs orientation="horizontal" selectedTabId={getSelectedTabId(pathname, forecastingPages)}>
          <TabList
            ariaLabel={intl.formatMessage({
              id: 'predict.predictions.tabs.aria_label',
              defaultMessage: 'Predictions'
            })}
            onTabChange={handleCarbonTabChange}
          >
            {visiblePages.map(page => (
              <Tab key={page.id} id={page.route} controls={`${page.route}-panel`} label={page.linkComponent} />
            ))}
          </TabList>
          {visiblePages.map(page => (
            <TabPanel key={page.id} id={`${page.route}-panel`} tabId={page.route} />
          ))}
        </Tabs>
        <div className={styles.carbonPredictionExplanationContainer}>
          <PredictionsExplanation
            showPredictionsExplanation={showPredictionsExplanation}
            togglePredictionsExplanation={handlePredictionExplanationsToggle}
            forecast={forecast}
            isConsolidation={isConsolidation}
          />
        </div>
      </Box>
    </Box>
  );
};
